import { AlertCircle, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/button";

type Props = {
  isError: boolean;
  message?: string;
  onRetry?: () => void;
  className?: string;
};

/**
 * 数据加载错误内联横幅。React Query isError=true 时渲染，附带可选的重试按钮。
 * 不再让网络异常静默——区分"加载失败"与"暂无数据"。
 */
export function QueryErrorBanner({ isError, message, onRetry, className }: Props) {
  const { t } = useTranslation();
  if (!isError) return null;
  const text = message ?? t("errors.queryFailed");
  return (
    // ★正文用 text-foreground 而非 text-destructive(2026-07-25 门 H-E 实测):红字在深色皮肤
    //   (cyber/comic)的淡红底上对比仅 3.3~3.6 < 4.5 —— 红色是固有难点(纯红 on 白也只 3.99)。
    //   错误语义靠红边框 + 红图标传达(符合「不只靠颜色」),正文用高对比前景色保证可读。
    <div
      role="alert"
      className={
        "flex items-center gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-foreground " +
        (className ?? "")
      }
    >
      <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
      <span className="flex-1 leading-snug">{text}</span>
      {onRetry && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-destructive hover:bg-destructive/20"
          onClick={onRetry}
        >
          <RotateCcw className="mr-1 h-3 w-3" />
          {t("actions.retry")}
        </Button>
      )}
    </div>
  );
}
